import React, { useEffect, useState } from "react";
import { TablePagination } from "@mui/material";
import { BiEdit, BiTrash } from "react-icons/bi";
import toast from "react-hot-toast";

import {
  getAllBranches,
  createBranch,
  deleteBranch,
  updateBranch,
} from "../../Api/branches";

import {
  getAllProjects,
  getProgramsByProject,
} from "../../Api/projectApi";

const PROJECT_COLORS = {
  "Shriram Finance": { bg: "#FBF3DE", border: "#C99A1E", accent: "#C99A1E", text: "#6B4E00" },
  Mahindra:         { bg: "#FCE9EB", border: "#C2263A", accent: "#C2263A", text: "#7A0018" },
  Linde:            { bg: "#E7EEF9", border: "#173E6E", accent: "#173E6E", text: "#173E6E" },
};
const DEFAULT_PROJECT_COLOR = { bg: "#EEF1F6", border: "#3D485C", accent: "#3D485C", text: "#1B2334" };
const PROJECT_NORMALIZE_MAP = {
  "Linde Hazmat": "Linde",
  "Linde Upskilling": "Linde",
  "SSSF Upskilling": "Shriram Finance",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500;1,9..144,600&family=IBM+Plex+Mono:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  .dash-root {
    --ink:#101826; --paper:#FFFFFF; --surface:#FFFFFF; --surface-alt:#FFFFFF;
    --brass:#2BB5CE; --brass-soft:#E6F7FA; --brass-deep:#1A869B;
    --teal:#2BB5CE; --teal-soft:#E6F7FA;
    --plum:#1E293B; --plum-soft:#F1F5F9;
    --rose:#B23A48; --rose-soft:#F6DEDF;
    --slate:#6B7686; --hairline:rgba(16,24,38,0.11);
    font-family:'Inter',sans-serif; min-height:100vh; color:var(--ink);
    background-color:#FFFFFF;
  }
  .dash-root * { box-sizing:border-box; }

  .eyebrow {
    font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:600;
    letter-spacing:0.16em; text-transform:uppercase; color:var(--brass-deep);
    display:flex; align-items:center; gap:8px; margin-bottom:8px;
  }
  .eyebrow::before { content:''; width:16px; height:1px; background:var(--brass); display:inline-block; }

  .section-title { font-family:'Fraunces',serif; font-style:italic; font-size:28px; font-weight:600; color:var(--ink); margin-bottom:4px; letter-spacing:-0.01em; }
  .section-sub   { font-size:13px; color:var(--slate); margin-bottom:26px; }

  .back-btn {
    display:inline-flex; align-items:center; gap:8px;
    background:var(--surface); border:1px solid var(--hairline); color:var(--slate);
    padding:9px 18px 8px; border-radius:6px; font-size:12.5px; font-weight:600;
    cursor:pointer; transition:all 0.18s ease;
    font-family:'IBM Plex Mono',monospace; letter-spacing:0.06em; text-transform:uppercase;
    box-shadow:0 1px 3px rgba(16,24,38,0.05); border-bottom:2px solid var(--hairline);
  }
  .back-btn:hover { border-color:var(--hairline); border-bottom-color:var(--brass); color:var(--brass-deep); }

  .dash-table { width:100%; border-collapse:separate; border-spacing:0; }
  .dash-table thead th {
    background:var(--surface-alt); color:var(--slate); font-family:'IBM Plex Mono',monospace; font-size:10.5px; font-weight:600;
    letter-spacing:0.12em; text-transform:uppercase; padding:14px 18px;
    border-bottom:1px solid var(--hairline);
  }
  .dash-table thead th:first-child { border-radius:14px 0 0 0; }
  .dash-table thead th:last-child  { border-radius:0 14px 0 0; }
  .dash-table tbody tr { transition:background 0.13s ease; }
  .dash-table tbody tr:hover td { background:var(--surface-alt); }
  .dash-table tbody td { padding:13px 18px; border-bottom:1px solid #F1F3F8; color:var(--ink); font-size:14px; background:var(--surface); }
  .dash-table tbody tr:last-child td { border-bottom:none; }
  .dash-table tbody tr:last-child td:first-child { border-radius:0 0 0 14px; }
  .dash-table tbody tr:last-child td:last-child  { border-radius:0 0 14px 0; }

  .badge-btn {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    border-radius: 6px;
    padding: 6px 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s ease;
  }

  .modal-box {
    background: #ffffff;
    border: 1px solid var(--hairline);
    border-radius: 6px 6px 20px 20px;
    box-shadow: 0 24px 64px rgba(16,24,38,0.18);
    overflow: hidden;
    width: 100%;
  }
  .modal-header {
    font-family: 'Fraunces', serif;
    font-style: italic;
    font-weight: 600;
    font-size: 19px;
    color: var(--ink);
    padding: 18px 24px;
    border-bottom: 1px solid var(--hairline);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .modal-body {
    padding: 24px;
  }
  .modal-footer {
    border-top: 1px solid var(--hairline);
    padding: 14px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  .input-field {
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--hairline);
    border-radius: 8px;
    padding: 11px 14px;
    font-size: 13px;
    color: var(--ink);
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .input-field:focus {
    border-color: var(--brass);
    box-shadow: 0 0 0 3px var(--brass-soft);
  }
  .input-field:disabled {
    background: var(--surface-alt);
    color: var(--slate);
    cursor: not-allowed;
  }
`;

const Branches = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /* ===== MODAL ===== */
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignBranch, setAssignBranch] = useState(null);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [assignProject, setAssignProject] = useState("");
  const [assignProgram, setAssignProgram] = useState("");
  const [assignProgramsList, setAssignProgramsList] = useState([]);

  /* ===== FORM ===== */
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [traineeName, setTraineeName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [username, setUsername] = useState("");
  const [project, setProject] = useState("");
  const [program, setProgram] = useState("");

  const [projects, setProjects] = useState([]);
  const [programs, setPrograms] = useState([]);

  /* ===== LOAD DATA ===== */
  const loadBranches = async () => {
    try {
      const res = await getAllBranches();
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error("Failed to load centers");
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    const res = await getAllProjects();
    const unique = [...new Set(res.data.map(p => p.projectName))];
    setProjects(unique);
  };

  useEffect(() => {
    loadBranches();
  }, []);

  /* ===== PAGINATION ===== */
  const paginated = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ===== CREATE ===== */
  const openCreateModal = async () => {
    setEditId(null);
    setName("");
    setLocation("");
    setTraineeName("");
    setEmail("");
    setMobile("");
    setUsername("");
    setProject("");
    setProgram("");
    setPrograms([]);

    await loadProjects();
    setOpen(true);
  };

  /* ===== PROJECT CHANGE ===== */
  const handleProjectChange = async (e) => {
    const value = e.target.value;
    setProject(value);
    setProgram("");

    if (value) {
      const res = await getProgramsByProject(value);
      setPrograms(res.data);
    } else {
      setPrograms([]);
    }
  };

  /* ===== EDIT ===== */
  const handleEdit = async (row) => {
    setEditId(row._id);
    setName(row.name);
    setLocation(row.location);
    setTraineeName(row.traineeName);
    setEmail(row.email);
    setMobile(row.mobile);
    setUsername(row.username || "");
    setProject(row.project || "");
    setProgram(row.program || "");

    await loadProjects();

    if (row.project) {
      const res = await getProgramsByProject(row.project);
      setPrograms(res.data);
    }

    setOpen(true);
  };

  /* ===== SAVE ===== */
  const handleSubmit = async () => {
    setSubmitting(true);

    const payload = {
      name,
      location,
      traineeName,
      mobile,
      email,
      username,
      project,
      program,
    };

    try {
      if (editId) {
        await updateBranch(editId, payload);
        toast.success("Center updated");
      } else {
        await createBranch(payload);
        toast.success("Center created");
      }
      setOpen(false);
      loadBranches();
    } catch {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  /* ===== DELETE ===== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this center?")) return;

    try {
      await deleteBranch(id);
      toast.success("Center deleted");
      loadBranches();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ===== ASSIGN PROJECTS ===== */
  const handleOpenAssignModal = async (row) => {
    setAssignBranch(row);
    let currentPairs = [];
    if (Array.isArray(row.project)) {
      currentPairs = row.project.map(item => {
        if (typeof item === 'string') {
          return { project: item, program: "" };
        }
        return { 
          project: item.project || item.projectName || "", 
          program: item.program || item.programName || "" 
        };
      });
    } else if (typeof row.project === 'string' && row.project) {
      currentPairs = [{ project: row.project, program: row.program || "" }];
    }
    setSelectedAssignments(currentPairs);
    setAssignProject("");
    setAssignProgram("");
    setAssignProgramsList([]);
    await loadProjects();
    setAssignModalOpen(true);
  };

  const handleAssignProjectChange = async (val) => {
    setAssignProject(val);
    setAssignProgram("");
    if (val) {
      const res = await getProgramsByProject(val);
      setAssignProgramsList(res.data);
    } else {
      setAssignProgramsList([]);
    }
  };

  const handleAddAssignment = () => {
    if (!assignProject || !assignProgram) {
      toast.error("Please select both project and program");
      return;
    }
    const duplicate = selectedAssignments.some(
      (item) => item.project === assignProject && item.program === assignProgram
    );
    if (duplicate) {
      toast.error("This project-program association is already assigned");
      return;
    }
    setSelectedAssignments((prev) => [
      ...prev,
      { project: assignProject, program: assignProgram }
    ]);
    setAssignProject("");
    setAssignProgram("");
    setAssignProgramsList([]);
  };

  const handleRemoveAssignment = (index) => {
    setSelectedAssignments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveAssignments = async () => {
    if (!assignBranch) return;
    setSubmitting(true);
    try {
      await updateBranch(assignBranch._id, { project: selectedAssignments });
      toast.success("Projects and programs assigned successfully");
      setAssignModalOpen(false);
      loadBranches();
    } catch {
      toast.error("Failed to assign projects and programs");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="dash-root" style={{ padding: "18px 18px" }}>
        
        {/* HEADER */}
        <div className="eyebrow">Registry · Centers</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "26px" }}>
          <div>
            <div className="section-title">Centers</div>
            <div className="section-sub" style={{ marginBottom: 0 }}>Manage training centers and user assignments</div>
          </div>
          <button 
            onClick={openCreateModal}
            style={{
              margin: 0,
              background: "#2BB5CE",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              fontWeight: 700,
              fontSize: "13px",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(43,181,206,0.32)",
              fontFamily: "'IBM Plex Mono', monospace"
            }}
          >
            + Create Center
          </button>
        </div>

        {/* TABLE */}
        <div style={{ borderRadius: "6px 6px 16px 16px", overflow: "hidden", border: "1px solid var(--hairline)", boxShadow: "0 2px 12px rgba(16,24,38,0.06)" }}>
          <table className="dash-table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "left" }}>S.No</th>
                <th style={{ textAlign: "left" }}>Center</th>
                <th style={{ textAlign: "left" }}>Location</th>
                <th style={{ textAlign: "left" }}>Project / Program</th>
                <th style={{ textAlign: "left" }}>Coordinator</th>
                <th style={{ textAlign: "left" }}>Contact</th>
                <th style={{ textAlign: "center", width: "240px" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length ? (
                paginated.map((row, index) => (
                  <tr key={row._id}>
                    {/* S.NO */}
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: "var(--slate)" }}>
                      {page * rowsPerPage + index + 1}
                    </td>

                    {/* CENTER */}
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ 
                          width: "36px", 
                          height: "36px", 
                          borderRadius: "50%", 
                          background: "var(--teal-soft)", 
                          color: "var(--teal)", 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          fontWeight: "bold",
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: "14px"
                        }}>
                          {row.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600, color: "var(--ink)", fontFamily: "'Fraunces', serif", fontSize: "15px" }}>{row.name}</span>
                      </div>
                    </td>

                    <td style={{ color: "var(--slate)", fontWeight: 500 }}>{row.location}</td>

                    {/* PROJECT / PROGRAM */}
                    <td>
                      {row.project ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {Array.isArray(row.project) ? (
                            row.project.map((item, idx) => {
                              const projName = typeof item === 'string' ? item : (item.project || item.projectName || "");
                              const progName = typeof item === 'string' ? "" : (item.program || item.programName || "");
                              const norm = PROJECT_NORMALIZE_MAP[projName] || projName;
                              const c = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                              return (
                                <div 
                                  key={idx} 
                                  style={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    background: c.bg, 
                                    border: `1px solid ${c.border}`, 
                                    borderRadius: "8px", 
                                    padding: "4px 10px", 
                                    color: c.text 
                                  }}
                                >
                                  <span style={{ fontSize: "11.5px", fontWeight: "700", fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {projName}
                                  </span>
                                  {progName && (
                                    <span style={{ fontSize: "9px", opacity: 0.8, fontWeight: "600", marginTop: "1px" }}>
                                      {progName}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            (() => {
                              const norm = PROJECT_NORMALIZE_MAP[row.project] || row.project;
                              const c = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                              return (
                                <div style={{ display: "flex", flexDirection: "column", background: c.bg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "4px 10px", color: c.text }}>
                                  <span style={{ fontSize: "11.5px", fontWeight: "700", fontFamily: "'IBM Plex Mono', monospace" }}>
                                    {row.project}
                                  </span>
                                  {row.program && (
                                    <span style={{ fontSize: "9px", opacity: 0.8, fontWeight: "600", marginTop: "1px" }}>
                                      {row.program}
                                    </span>
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td style={{ color: "var(--ink)", fontWeight: 500 }}>{row.traineeName}</td>

                    {/* CONTACT */}
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 600, color: "var(--ink)", fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px" }}>
                          {row.mobile || "—"}
                        </span>
                        {row.email && (
                          <span style={{ fontSize: "11px", color: "var(--slate)", marginTop: "2px" }}>
                            {row.email}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* ACTIONS */}
                    <td>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                        <button
                          onClick={() => handleEdit(row)}
                          title="Edit Center"
                          className="badge-btn"
                          style={{
                            background: "var(--teal-soft)",
                            border: "1px solid rgba(31,111,100,0.28)",
                            color: "var(--teal)"
                          }}
                          onMouseOver={(e) => e.currentTarget.style.filter = "brightness(0.95)"}
                          onMouseOut={(e) => e.currentTarget.style.filter = "none"}
                        >
                          <BiEdit size={14} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(row._id)}
                          title="Delete Center"
                          className="badge-btn"
                          style={{
                            background: "var(--rose-soft)",
                            border: "1px solid rgba(178,58,72,0.28)",
                            color: "var(--rose)"
                          }}
                          onMouseOver={(e) => e.currentTarget.style.filter = "brightness(0.95)"}
                          onMouseOut={(e) => e.currentTarget.style.filter = "none"}
                        >
                          <BiTrash size={14} /> Delete
                        </button>
                        <button
                          onClick={() => handleOpenAssignModal(row)}
                          className="badge-btn"
                          style={{
                            background: "var(--brass-soft)",
                            border: "1px solid rgba(184,134,46,0.32)",
                            color: "var(--brass-deep)"
                          }}
                          onMouseOver={(e) => e.currentTarget.style.filter = "brightness(0.95)"}
                          onMouseOut={(e) => e.currentTarget.style.filter = "none"}
                        >
                          Assign
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ py: 40, textAlign: "center", color: "var(--slate)", fontWeight: "500" }}>
                    {loading ? "Loading centers..." : "No centers found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          <div style={{ background: "var(--surface)", borderTop: "1px solid var(--hairline)" }}>
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </div>
        </div>

        {/* MODAL: CREATE / EDIT */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" style={{ backdropFilter: "blur(2px)" }}>
            <div className="modal-box" style={{ maxWidth: "600px" }}>
              <div className="modal-header">
                <span>{editId ? "Edit Center" : "Create Center"}</span>
                <button 
                  onClick={() => setOpen(false)} 
                  style={{ background: "none", border: "none", color: "var(--slate)", fontSize: "20px", cursor: "pointer", fontWeight: "bold" }}
                >
                  &times;
                </button>
              </div>

              <div className="modal-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Center Name</label>
                  <input className="input-field" placeholder="e.g. Vellore Center" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Location</label>
                  <input className="input-field" placeholder="e.g. Vellore" value={location} onChange={(e) => setLocation(e.target.value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Coordinator</label>
                  <input className="input-field" placeholder="Coordinator Name" value={traineeName} onChange={(e) => setTraineeName(e.target.value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Mobile</label>
                  <input className="input-field" placeholder="Contact number" value={mobile} onChange={(e) => setMobile(e.target.value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Email Address</label>
                  <input className="input-field" type="email" placeholder="email@address.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Username</label>
                  <input className="input-field" placeholder="Login username" disabled={!!editId} value={username} onChange={(e) => setUsername(e.target.value)} />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Default Project</label>
                  <select className="input-field" value={project} onChange={handleProjectChange}>
                    <option value="">Select Project</option>
                    {projects.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "11px", fontWeight: "600", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>Default Program</label>
                  <select className="input-field" value={program} onChange={(e) => setProgram(e.target.value)} disabled={!project}>
                    <option value="">Select Program</option>
                    {programs.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  onClick={() => setOpen(false)} 
                  style={{ background: "none", border: "none", color: "var(--slate)", padding: "10px 18px", cursor: "pointer", fontWeight: "600" }}
                >
                  Cancel
                </button>
                <button 
                  disabled={submitting} 
                  onClick={handleSubmit}
                  style={{
                    background: "var(--brass)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px 22px",
                    fontWeight: "600",
                    cursor: submitting ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 14px rgba(184,134,46,0.32)"
                  }}
                >
                  {editId ? "Update Center" : "Create Center"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: ASSIGN PROJECTS & PROGRAMS */}
        {assignModalOpen && assignBranch && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" style={{ backdropFilter: "blur(2px)" }}>
            <div className="modal-box" style={{ maxWidth: "450px" }}>
              <div className="modal-header">
                <span>Assign Projects to {assignBranch.name}</span>
                <button 
                  onClick={() => setAssignModalOpen(false)} 
                  style={{ background: "none", border: "none", color: "var(--slate)", fontSize: "20px", cursor: "pointer", fontWeight: "bold" }}
                >
                  &times;
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: "400px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "18px" }}>
                
                {/* Add New Assignment */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "14px", background: "var(--surface-alt)", border: "1px solid var(--hairline)", borderRadius: "10px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--brass-deep)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>
                    Add New Assignment
                  </span>
                  
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <select 
                      className="input-field" 
                      value={assignProject} 
                      onChange={(e) => handleAssignProjectChange(e.target.value)}
                    >
                      <option value="">Select Project</option>
                      {projects.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    
                    <select 
                      className="input-field" 
                      value={assignProgram} 
                      onChange={(e) => setAssignProgram(e.target.value)}
                      disabled={!assignProject}
                    >
                      <option value="">Select Program</option>
                      {assignProgramsList.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>

                    <button 
                      onClick={handleAddAssignment}
                      style={{
                        background: "var(--brass)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 14px",
                        fontWeight: "600",
                        fontSize: "12.5px",
                        cursor: "pointer"
                      }}
                    >
                      + Add Config
                    </button>
                  </div>
                </div>

                {/* Assignment List */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--slate)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace" }}>
                    Assigned Configs ({selectedAssignments.length})
                  </span>

                  {selectedAssignments.length === 0 ? (
                    <div style={{ padding: "20px", border: "1px dashed var(--hairline)", borderRadius: "10px", textAlign: "center", color: "var(--slate)", fontStyle: "italic", fontSize: "13px" }}>
                      No projects or programs assigned.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {selectedAssignments.map((item, index) => {
                        const norm = PROJECT_NORMALIZE_MAP[item.project] || item.project;
                        const c = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                        return (
                          <div 
                            key={index} 
                            style={{ 
                              display: "flex", 
                              justifyContent: "space-between", 
                              alignItems: "center", 
                              padding: "10px 14px", 
                              border: `1px solid ${c.border}`, 
                              background: c.bg,
                              borderRadius: "8px", 
                              color: c.text
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <span style={{ fontSize: "12.5px", fontWeight: "750", fontFamily: "'IBM Plex Mono', monospace" }}>{item.project}</span>
                              <span style={{ fontSize: "10px", opacity: 0.85, fontWeight: "600" }}>{item.program || "No program"}</span>
                            </div>
                            <button 
                              onClick={() => handleRemoveAssignment(index)}
                              style={{ 
                                background: "rgba(239, 68, 68, 0.1)", 
                                border: "1px solid rgba(239, 68, 68, 0.2)", 
                                borderRadius: "4px", 
                                color: "#ef4444", 
                                padding: "4px 8px", 
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center"
                              }}
                              title="Remove Assignment"
                            >
                              <BiTrash size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  onClick={() => setAssignModalOpen(false)} 
                  style={{ background: "none", border: "none", color: "var(--slate)", padding: "10px 18px", cursor: "pointer", fontWeight: "600" }}
                >
                  Cancel
                </button>
                <button 
                  disabled={submitting} 
                  onClick={handleSaveAssignments}
                  style={{
                    background: "var(--brass)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px 22px",
                    fontWeight: "600",
                    cursor: submitting ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 14px rgba(184,134,46,0.32)"
                  }}
                >
                  Save Assignments
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Branches;
